import { useEffect, useState } from 'react';
import { shiftAPI, getApiErrorMessage } from '@matix/api-client';
import type { ZReportDto } from '@matix/api-client';
import { useUI } from '../../store/ui';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { formatDateTime } from '@matix/utils';
import { printReceipt, buildZReportHtml, documentRangeSentence } from '@matix/receipt';
import { useCurrency } from '../print/useCurrency';
import { useTenantConfig } from '../../store/tenantConfig';
import { XIcon } from '../../components/icons';

/**
 * Z-Report modal — Phase 10.10. Fetches /api/v1/shifts/:id/z-report (which
 * also bumps printed_count server-side) and renders a screen view + a
 * print button that hands off to the existing iframe printer pipeline.
 *
 * The shift_id is staged on `window.__pos_zreport_shift_id` by the caller
 * before showModal('zreport'): ShiftCloseScreen on close, and the Reports
 * screen's "My Shifts" list for a reprint.
 *
 * Two print buttons: the till's 80 mm roll and an A4 sheet. Same report,
 * same figures, laid out for whichever printer is nearest — the roll
 * stylesheet on an office printer is a 72 mm strip of small type down the
 * middle of a blank page.
 */
export function ZReportModal() {
  const isOpen = useUI((s) => s.openModal === 'zreport');
  const closeModal = useUI((s) => s.closeModal);
  const setScreen = useUI((s) => s.setScreen);

  const [report, setReport] = useState<ZReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const currency = useCurrency();
  // PrintShiftSerial — the serial is an internal reference, off unless asked for.
  const printShiftSerial = useTenantConfig((s) => s.printShiftSerial);
  const { t, lang } = useLang();

  useEffect(() => {
    if (!isOpen) {
      setReport(null);
      return;
    }
    const shiftId = (window as unknown as { __pos_zreport_shift_id?: number }).__pos_zreport_shift_id;
    if (!shiftId) {
      toast(t({ en: 'No shift id available for Z-Report', ar: 'لا يوجد معرّف وردية متاح لتقرير Z' }), 'error');
      return;
    }
    setLoading(true);
    shiftAPI.zReport(shiftId)
      .then(setReport)
      .catch((err) => toast(getApiErrorMessage(err), 'error'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  async function handlePrint(paper: 'roll' | 'a4') {
    if (!report) return;
    try {
      await printReceipt(
        buildZReportHtml(report, currency, { showShiftSerial: printShiftSerial, paper, lang }),
      );
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    }
  }

  function handleDone() {
    closeModal();
    setScreen('app');
  }

  return (
    <div className="modal-overlay show" onClick={handleDone}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--success-light)', color: 'var(--success)' }}
          >
            Z
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{t({ en: 'Z-Report', ar: 'تقرير Z' })}</div>
            <div className="modal-header-name-ar">
              {report ? report.header.shift_code : t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={handleDone}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div style={{ padding: 24, color: 'var(--text-muted)' }}>{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>
          )}
          {report && <ZReportPreview report={report} currency={currency} />}
        </div>

        <div className="modal-footer">
          <div />
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={handleDone}>
              {t({ en: 'Done', ar: 'تم' })}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void handlePrint('a4')}
              disabled={!report}
            >
              {t({ en: 'Print A4', ar: 'طباعة A4' })}
            </button>
            <button
              type="button"
              className="btn-primary-modal"
              onClick={() => void handlePrint('roll')}
              disabled={!report}
            >
              {t({ en: 'Print', ar: 'طباعة' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ZReportPreview({
  report,
  currency,
}: {
  report: ZReportDto;
  currency: { code: string; precision: number };
}) {
  const { t, lang } = useLang();
  const digits = Math.max(0, Math.min(8, currency.precision));
  const fmt = (n: number): string =>
    (Number.isFinite(n) ? n : 0).toFixed(digits);
  // refunds_cash is stored as a positive magnitude; it reads as money leaving
  // the drawer, so the report shows it negative.
  const refunds = -report.sales.refunds_cash;
  return (
    <div className="zreport">
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{report.header.shift_code}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {report.header.branch_name} · {report.header.cashier_name} · {report.header.drawer_name}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }} dir="ltr">
          {formatDateTime(report.header.opened_at)}
          {report.header.closed_at ? ` → ${formatDateTime(report.header.closed_at)}` : ''}
        </div>
        {/* The invoice span identifies the document the way the branch and the
            cashier do, so it belongs in the header with them — directly under
            the closing time, as on the printed page. One line per receipt
            code, never merged: each code runs its own Entry_No sequence, so a
            shift with a return shows two spans rather than one bridging both. */}
        {(report.document_ranges ?? [])
          .filter((r) => r.first_no != null && r.last_no != null)
          .map((r) => (
            <div
              key={r.code}
              dir={lang === 'ar' ? 'rtl' : 'ltr'}
              style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}
            >
              {documentRangeSentence(r, lang)}
            </div>
          ))}
      </div>

      <Section title={t({ en: 'Sales', ar: 'المبيعات' })}>
        {/* Each payment method under the tenant's own name (2026-09-28) —
            the fixed headings bucketed by Code and called khwada's "LINK"
            "Wallet". The fixed rows stay as the fallback for an older API. */}
        {report.breakdowns?.by_payment_method && report.breakdowns.by_payment_method.length > 0 ? (
          report.breakdowns.by_payment_method.map((m) => (
            <Row key={m.key} l={(lang === 'ar' && m.label_ar?.trim()) || m.label} v={fmt(m.total)} />
          ))
        ) : (
          <>
            <Row l={t({ en: 'Cash', ar: 'نقدي' })}            v={fmt(report.sales.cash)} />
            <Row l={t({ en: 'Card', ar: 'بطاقة' })}            v={fmt(report.sales.card)} />
            <Row l={t({ en: 'Wallet', ar: 'محفظة' })}          v={fmt(report.sales.wallet)} />
            <Row l={t({ en: 'Credit', ar: 'آجل' })}          v={fmt(report.sales.credit)} />
            {/* Only when the tenant actually used a method outside the four. */}
            {report.sales.other !== 0 && (
              <Row l={t({ en: 'Other', ar: 'أخرى' })}        v={fmt(report.sales.other)} />
            )}
          </>
        )}
        <Row l={t({ en: 'Refunds (cash)', ar: 'المرتجعات (نقدي)' })}  v={fmt(refunds)} />
        <Row l={t({ en: 'Gross sales', ar: 'إجمالي المبيعات' })}     v={fmt(report.sales.gross_sales)} bold />
        <Row l={t({ en: 'Discounts', ar: 'الخصومات' })}       v={fmt(-report.sales.discounts_total)} />
        <Row l={t({ en: 'VAT', ar: 'ضريبة القيمة المضافة' })}             v={fmt(report.sales.vat_total)} />
        <Row l={t({ en: 'Net sales', ar: 'صافي المبيعات' })}       v={fmt(report.sales.net_sales)} bold />
      </Section>

      {/* Receipt counts and the float/pickup/drop/expense components are kept
          in the snapshot for audit but off the report: none of them feed
          expected cash any more, so printing them only invites the reader to
          re-derive a number they no longer explain. */}
      <Section title={t({ en: 'Cash drawer', ar: 'درج النقدية' })}>
        <Row l={t({ en: 'Expected', ar: 'المتوقع' })}      v={fmt(report.cash_drawer.expected_cash)} bold />
        <Row l={t({ en: 'Counted', ar: 'المعدود' })}       v={fmt(report.cash_drawer.counted_cash)} bold />
        <Row l={t({ en: 'Variance', ar: 'الفرق' })}      v={fmt(report.cash_drawer.variance)} bold />
      </Section>

      {report.denominations.length > 0 && (
        <Section title={t({ en: 'Denominations', ar: 'الفئات' })}>
          {report.denominations.map((d) => (
            <Row
              key={d.denomination}
              l={`${d.denomination.toFixed(digits)} × ${d.count}`}
              v={fmt(d.subtotal)}
            />
          ))}
        </Section>
      )}

      {report.adjustments.length > 0 && (
        <Section title={t({ en: 'Adjustments (post-close)', ar: 'التعديلات (بعد الإغلاق)' })}>
          {report.adjustments.map((a) => (
            <div key={a.receipt_id + a.changed_at} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px dotted var(--border)' }}>
              <div><b>{a.receipt_code ?? `#${a.receipt_id}`}</b> · {a.changed_at}</div>
              <div style={{ color: 'var(--text-muted)' }}>
                {a.old_label ?? '—'} → {a.new_label ?? '—'}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                {t({ en: 'by', ar: 'بواسطة' })} {a.changed_by_name ?? '—'} · {t({ en: 'approved by', ar: 'اعتمده' })} {a.approved_by_name ?? '—'}
                {a.reason ? ` · ${a.reason}` : ''}
              </div>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="zreport-section">
      <div className="zreport-section-title">{title}</div>
      {children}
    </div>
  );
}

function Row({ l, v, bold }: { l: string; v: string; bold?: boolean }) {
  return (
    <div className={`zreport-row${bold ? ' bold' : ''}`}>
      <span className="l">{l}</span>
      <span>{v}</span>
    </div>
  );
}

